import type { TargetingFieldsValues, TargetingReadOnlyProps } from '@openx/types/targeting/targeting';
import { displayContexts } from '@openx/types/targeting/targeting';
import {
  type TechnologyAndDevicesFormState,
  TechnologyAndDevicesOption,
} from '@openx/types/targeting/technologyAndDevices';
import { noop } from '@openx/utils/noop';
import isObjectEmpty from 'lodash/isEmpty';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParameterRemove } from '../../utils/hooks/useParameterRemove';
import { useTargetingChildDisabledState } from '../../utils/hooks/useTargetingChildDisabledState';
import { useTargetingDisabledState } from '../../utils/hooks/useTargetingDisabledState';
import { CtvAlert } from '../shared/CtvValidation/CtvAlert';
import type { CtvValidationReturnType } from '../shared/CtvValidation/utils/useCtvValidation';
import { DimensionChipLine } from '../shared/DimensionChip/DimensionChipLine';
import { useGetFilteredFields } from '../shared/hooks/useGetFilteredFields';
import { useDropdownOpen } from '../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen';
import { StyledReadOnlyTargetingDropdown } from '../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown';
import { TargetingDropdownSection } from '../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection';
import { TargetingReadOnly } from '../shared/TargetingReadOnly/TargetingReadOnly';
import { isEmptyState } from '../shared/Technographic/utils';
import { Items } from './Items';
import { useTypeOptionDropdown } from './TechnologyAndDevicesTopBox/hooks/useTypeOptionDropdown';

type CtvValidationMethods = Pick<
  CtvValidationReturnType,
  'setIsFormatTypeOpen' | 'shouldDisplayWarningInTechnographic'
>;

const defaultCustomValidationMethods: CtvValidationMethods = {
  setIsFormatTypeOpen: noop,
  shouldDisplayWarningInTechnographic: false,
};

type TechnologyAndDevicesReadOnlyProps = TargetingReadOnlyProps<TechnologyAndDevicesFormState, CtvValidationMethods> & {
  field: TargetingFieldsValues;
  isNewFormat: boolean;
  onRemoveParameter?: (type: TechnologyAndDevicesOption) => void;
};

export const TechnologyAndDevicesReadOnly = ({
  name,
  field,
  onOpenDrawerClick,
  onRemoveClick,
  targetingParams,
  readonly,
  customValidationMethods = defaultCustomValidationMethods,
  displayContext,
  isNewFormat,
  onRemoveParameter,
}: TechnologyAndDevicesReadOnlyProps) => {
  const { isDisabled, disabledReasonMessage, hasError } = useTargetingDisabledState(field);

  const { getDisabledStateForOption } = useTargetingChildDisabledState(field);

  const isEmpty = isEmptyState(targetingParams);
  const targetingParamsFiltered = useGetFilteredFields(targetingParams || {});
  const { t } = useTranslation();
  const { shouldDisplayWarningInTechnographic, setIsFormatTypeOpen } = customValidationMethods;

  const { optionsKeys, getOptionLabel } = useTypeOptionDropdown(isNewFormat, displayContext);

  const availableTargetingParams = useMemo(() => {
    if (!targetingParams) return [];

    return Object.entries(targetingParams).filter(([key]) => optionsKeys.includes(key as TechnologyAndDevicesOption));
  }, [optionsKeys, targetingParams]);

  const configuredParams = useMemo(() => {
    return availableTargetingParams.filter(([type, data]) => {
      const value = data.val;

      if (type === TechnologyAndDevicesOption.USER_AGENT) {
        return !isObjectEmpty(value);
      }

      return value instanceof Set ? value.size > 0 : false;
    });
  }, [availableTargetingParams]);

  const configuredParamKeys = useMemo(() => new Set(configuredParams.map(([type]) => type)), [configuredParams]);

  const shouldShowLabelAfterIndex = (index: number) => {
    const nextIndex = index + 1;
    if (nextIndex >= availableTargetingParams.length) return false;

    const hasConfiguredUpToIndex = availableTargetingParams
      .slice(0, index + 1)
      .some(([type]) => configuredParamKeys.has(type));

    const nextIsConfigured = configuredParamKeys.has(availableTargetingParams[nextIndex][0]);

    return hasConfiguredUpToIndex && nextIsConfigured;
  };

  const optionsCount = targetingParamsFiltered.length;
  const hasValue = optionsCount !== undefined && optionsCount > 0;
  const { isOpen, toggleOpen } = useDropdownOpen(hasValue);

  const removeParameterHandler = useParameterRemove<TechnologyAndDevicesOption>(onRemoveParameter, onRemoveClick);

  if (!readonly)
    return (
      <TargetingReadOnly
        name={name}
        field={field}
        readonly={readonly}
        isEmpty={isEmpty}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemoveClick}
        isDisabled={isDisabled || hasError}
        description={t(
          'Tailor delivery to users based on the technology they use, such as operating systems or browsers.'
        )}
        optionsCount={optionsCount}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        disabledReasonMessage={disabledReasonMessage}
      >
        {isOpen && (
          <StyledReadOnlyTargetingDropdown>
            {availableTargetingParams.map(([type, data], index) => {
              const optionType = type as TechnologyAndDevicesOption;
              const shouldAddDimensionChipLine = availableTargetingParams.length - 1 > index;
              const shouldDisplayWarning =
                shouldDisplayWarningInTechnographic && optionType === TechnologyAndDevicesOption.DEVICE_TYPE;

              const value = data.val;
              const isEmpty =
                optionType === TechnologyAndDevicesOption.USER_AGENT
                  ? isObjectEmpty(value)
                  : value instanceof Set && value.size === 0;

              const {
                isDisabled: isSectionDisabled,
                disabledReasonMessage: sectionDisabledReasonMessage,
                errorReasonMessage: sectionErrorReasonMessage,
              } = getDisabledStateForOption(optionType);

              if (readonly && isEmpty) return null;

              return (
                <Fragment key={type}>
                  <TargetingDropdownSection
                    name={getOptionLabel(optionType)}
                    hideButtons={readonly}
                    isEmpty={isEmpty}
                    onOpenDrawerClick={() => onOpenDrawerClick?.(type)}
                    onRemove={removeParameterHandler(optionType)}
                    isDisabled={isSectionDisabled}
                    disabledReasonMessage={sectionDisabledReasonMessage}
                    hasOptionsSelected={!isEmpty}
                  >
                    <div>
                      {targetingParams && (
                        <Items
                          targetingParams={targetingParams}
                          type={optionType}
                          disabledReasonMessage={sectionErrorReasonMessage}
                        />
                      )}

                      {shouldDisplayWarning && (
                        <CtvAlert
                          invalidField={t('Distribution Channel')}
                          invalidParams={
                            displayContext === displayContexts.TRAFFIC_SET
                              ? [t('Ad Placement = Video'), t('Distribution Channel = Application')]
                              : [t('Format = Video'), t('Channel = Application')]
                          }
                          onOpen={setIsFormatTypeOpen}
                          dataTest={'ctv-alert-in-technology-and-devices'}
                        />
                      )}
                    </div>
                  </TargetingDropdownSection>

                  {shouldAddDimensionChipLine && <DimensionChipLine showLabel={shouldShowLabelAfterIndex(index)} />}
                </Fragment>
              );
            })}
          </StyledReadOnlyTargetingDropdown>
        )}
      </TargetingReadOnly>
    );

  return (
    <TargetingReadOnly
      name={name}
      field={field}
      readonly={readonly}
      isEmpty={isEmpty}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemoveClick}
      isDisabled={isDisabled || hasError}
      description={t(
        'Tailor delivery to users based on the technology they use, such as operating systems or browsers.'
      )}
      disabledReasonMessage={disabledReasonMessage}
    >
      {targetingParams &&
        !isEmpty &&
        targetingParamsFiltered.map((type, index) => {
          const shouldAddDimensionChipLine = targetingParamsFiltered.length - 1 > index;
          const shouldDisplayWarning =
            shouldDisplayWarningInTechnographic && type === TechnologyAndDevicesOption.DEVICE_TYPE;

          const { errorReasonMessage: sectionErrorReasonMessage } = getDisabledStateForOption(type);

          return (
            <div key={type}>
              <Items
                targetingParams={targetingParams}
                type={type}
                displayContext={displayContext}
                disabledReasonMessage={sectionErrorReasonMessage}
              />

              {shouldDisplayWarning && (
                <CtvAlert
                  invalidField={t('Distribution Channel')}
                  invalidParams={
                    displayContext === displayContexts.TRAFFIC_SET
                      ? [t('Ad Placement = Video'), t('Distribution Channel = Application')]
                      : [t('Format = Video'), t('Channel = Application')]
                  }
                  onOpen={setIsFormatTypeOpen}
                  dataTest={'ctv-alert-in-technology-and-devices'}
                />
              )}

              {shouldAddDimensionChipLine && <DimensionChipLine />}
            </div>
          );
        })}
    </TargetingReadOnly>
  );
};
